import { useCallback, useState } from 'react';

import './styles.scss';
import {
    TSideMenu,
    TypeMenu,
} from '@celebration/design-system/dist/lib/side-menu/props.types';
import { Link } from 'react-router-dom';
import { ClbButton } from '@celebration/react/ClbButton/ClbButton';
import { ClbAvatar } from '@celebration/react/ClbAvatar/ClbAvatar';
import { ClbIcon } from '@celebration/react/ClbIcon/ClbIcon';
import NotificationsDrawer from './components/notifications';
import { ClbHeaderProps } from './ClbHeaderProps';
import {
    disablePageScrollY,
    enablePageScrollY,
} from '../../../utils/page-utils';
import SideMenu from '../side-menu/side-menu';
import { useNotifications } from '../../../app/contexts/notifications/notifications.context';

function ClbHeader({
    productName = '',
    username = '',
    userProfile = '',
    avatarMenuOptions = [],
    basename = '/',
    activePath = '',
    logo = '',
    enableUserImage = false,
    empty = false,
    withSideMenu = false,
    enableAvatar = false,
    links = [],
    dataTestId = '',
}: Readonly<ClbHeaderProps>) {
    const { newNotifications, updateNotificationsCounter } = useNotifications();

    const [menuOpened, setMenuOpened] = useState(false);
    const [mobileSubNavigation, setMobileSubNavigation] = useState(false);
    const [openDrawer, setOpenDrawer] = useState(false);

    const closeNoificationDrawer = useCallback(() => {
        setOpenDrawer(false);
        enablePageScrollY();
    }, [setOpenDrawer]);

    const openNoificationDrawer = useCallback(() => {
        setOpenDrawer(true);
        disablePageScrollY();
    }, [setOpenDrawer]);

    const toggleMenu = () =>
        mobileSubNavigation
            ? setMobileSubNavigation(false)
            : setMenuOpened(!menuOpened);

    const closeMenu = () => {
        updateNotificationsCounter();
        setMenuOpened(false);
    };
    const labelContent = (link: TSideMenu, index: number) => {
        if (!withSideMenu && link.options) {
            return link.options[index].label;
        }
        return link.label;
    };

    const renderMenuLinks = () => {
        return links.map((link, index) => {
            if (!link.hidden) {
                return (
                    <li
                        key={link.id}
                        className={`Header__nav-item ${
                            link.opened ? 'Header__nav-item--selected' : ''
                        }`}
                    >
                        <Link
                            to={link.path}
                            target={link.target}
                            className={`Header__nav-link ${
                                link.opened ? 'Header__nav-link--selected' : ''
                            }`}
                        >
                            {labelContent(link, index) ?? ''}
                        </Link>
                    </li>
                );
            }
            return null;
        });
    };

    const renderNotifications = useCallback(() => {
        return (
            <div
                data-testid="notifications"
                className={`Header__notifications${
                    newNotifications ? ' Header__notifications--new' : ''
                }`}
            >
                <ClbButton
                    id="buttonNotification"
                    size="md"
                    icon="notification"
                    styleType="icon"
                    onClick={openNoificationDrawer}
                    data-testid="buttonNotification"
                />
            </div>
        );
    }, [newNotifications, openNoificationDrawer]);

    const renderAvatar = () => {
        return (
            <div className="Header__avatar" data-testid="avatar">
                <ClbAvatar
                    showFullLabel
                    label={username}
                    url={enableUserImage ? userProfile : undefined}
                    dropdownPosition="right"
                    size="md"
                    links={avatarMenuOptions.map(option => ({
                        label: option.label,
                        route: option?.href ?? '',
                        onClick: option.callback,
                    }))}
                />
            </div>
        );
    };

    return (
        <>
            <header
                className={`Header ${menuOpened ? 'Header--open' : ''}`}
                data-testid={dataTestId}
            >
                <div className="Header__left">
                    {links.length > 0 && !empty && (
                        <button
                            type="button"
                            className="Header__mobile-menu"
                            onClick={toggleMenu}
                            aria-label="Abrir menu / Fechar menu"
                        >
                            <ClbIcon
                                icon={menuOpened ? 'close' : 'menu'}
                                size="lg"
                            />
                        </button>
                    )}

                    <div className="Header__product-info">
                        <a className="Header__logo" href={basename}>
                            <img
                                className="Header__logo-img"
                                src={logo}
                                alt="Logo"
                            />
                        </a>
                        <h3 className="Header__title">{productName}</h3>
                    </div>

                    {!empty && (
                        <nav
                            className="Header__nav"
                            aria-label="Navigation links"
                        >
                            <ul className="Header__navlist">
                                {renderMenuLinks()}
                            </ul>
                        </nav>
                    )}

                    {!empty && (
                        <SideMenu
                            links={links}
                            activePath={activePath}
                            typeMenu={'CategoryMenu' as TypeMenu}
                            className="Header__side-menu"
                        />
                    )}
                </div>
                {!empty && (
                    <div className="Header__right">
                        {renderNotifications()}
                        {enableAvatar && renderAvatar()}
                    </div>
                )}
            </header>

            {openDrawer && (
                <NotificationsDrawer
                    open={openDrawer}
                    handleCloseDrawer={closeNoificationDrawer}
                    closeParent={closeMenu}
                />
            )}
        </>
    );
}

export default ClbHeader;
